import React, { useEffect, useMemo, useRef, useState } from 'react';
import AssistantDropdown from './AssistantDropdown';
import MessageItem from './MessageItem';
import { getAgentV3, sendAgentChat } from '../services/api';
import './Conversation.css';
import './ChatTest.css';

const DEVICE_OPTIONS = [
  { value: 'webchat', label: 'webchat', product: 'webchat' },
  { value: 'sms', label: 'sms', product: 'webchat' },
  { value: 'messaging', label: 'messaging', product: 'webchat' },
  { value: 'facebook', label: 'facebook', product: 'facebook' },
  { value: 'talklive', label: 'talklive', product: 'talklive' },
  { value: 'marketplace', label: 'marketplace', product: 'marketplace' },
  { value: 'campaign', label: 'campaign', product: 'campaign' },
  { value: 'phenix', label: 'phenix', product: 'phenix' },
  { value: 'hook', label: 'hook', product: 'hook' },
];

function newThreadId() {
  return `thread_admin_test_${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}`;
}

function packLabel(pack, index) {
  const label = pack.label || pack.id || `Pack ${index + 1}`;
  const id = pack.id ? ` (${pack.id})` : '';
  const off = pack.enabled === false ? ' — off' : '';
  return `${label}${id}${off}`;
}

function isProductEnabled(products, key) {
  if (!products) return false;
  const value = products[key];
  if (Array.isArray(value)) return value.some((p) => p?.enabled);
  return Boolean(value?.enabled);
}

function ChatTest() {
  const [assistantId, setAssistantId] = useState('');
  const [agentV3, setAgentV3] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [webchatPackId, setWebchatPackId] = useState('');
  const [facebookPackId, setFacebookPackId] = useState('');
  const [sourceUrl, setSourceUrl] = useState('https://www.example-dealer.ca');
  const [device, setDevice] = useState('webchat');
  const [threadId, setThreadId] = useState(newThreadId);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const webchatPacks = useMemo(
    () => (Array.isArray(agentV3?.products?.webchat) ? agentV3.products.webchat : []),
    [agentV3]
  );
  const facebookPacks = useMemo(
    () => (Array.isArray(agentV3?.products?.facebook) ? agentV3.products.facebook : []),
    [agentV3]
  );

  const selectedWebchat = useMemo(
    () => webchatPacks.find((p) => p.id === webchatPackId) || webchatPacks[0] || null,
    [webchatPacks, webchatPackId]
  );
  const selectedFacebook = useMemo(
    () => facebookPacks.find((p) => p.id === facebookPackId) || facebookPacks[0] || null,
    [facebookPacks, facebookPackId]
  );

  const enabledFlags = useMemo(() => {
    const products = agentV3?.products;
    return DEVICE_OPTIONS.reduce((acc, opt) => {
      acc[opt.product] = isProductEnabled(products, opt.product);
      return acc;
    }, {});
  }, [agentV3]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const resetChat = () => {
    setThreadId(newThreadId());
    setMessages([]);
    setError('');
  };

  const applyPackUrl = (pack, fallbackDealerUrl) => {
    const url = (pack?.site_url || fallbackDealerUrl || '').trim();
    if (url) setSourceUrl(url);
  };

  const loadAgent = async (id) => {
    if (!id) {
      setAgentV3(null);
      setWebchatPackId('');
      setFacebookPackId('');
      return;
    }
    setAgentLoading(true);
    setError('');
    try {
      const res = await getAgentV3(id);
      const data = res.data?.data?.[0] || null;
      setAgentV3(data);
      const webchats = Array.isArray(data?.products?.webchat) ? data.products.webchat : [];
      const facebooks = Array.isArray(data?.products?.facebook) ? data.products.facebook : [];
      const primaryWeb =
        webchats.find((p) => p.enabled) || webchats[0] || null;
      const primaryFb =
        facebooks.find((p) => p.enabled) || facebooks[0] || null;
      setWebchatPackId(primaryWeb?.id || '');
      setFacebookPackId(primaryFb?.id || '');
      applyPackUrl(primaryWeb, data?.dealer_url);
    } catch (err) {
      setAgentV3(null);
      const detail = err.response?.data?.detail || err.message || 'Failed to load agent';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setAgentLoading(false);
    }
  };

  const handleAssistantChange = (id) => {
    setAssistantId(id);
    setThreadId(newThreadId());
    setMessages([]);
    setError('');
    setDevice('webchat');
    loadAgent(id);
  };

  const handleWebchatPackChange = (packId) => {
    setWebchatPackId(packId);
    const pack = webchatPacks.find((p) => p.id === packId);
    applyPackUrl(pack, agentV3?.dealer_url);
    resetChat();
  };

  const handleFacebookPackChange = (packId) => {
    setFacebookPackId(packId);
    resetChat();
  };

  const handleDeviceChange = (nextDevice) => {
    setDevice(nextDevice);
    if (nextDevice === 'webchat' || nextDevice === 'sms' || nextDevice === 'messaging') {
      applyPackUrl(selectedWebchat, agentV3?.dealer_url);
    }
    resetChat();
  };

  const sendMessage = async () => {
    const text = message.trim();
    if (!text || loading) return;
    if (!assistantId) {
      setError('Select an assistant first.');
      return;
    }

    const userBubble = {
      role: 'user',
      value: text,
      created_at: Math.floor(Date.now() / 1000),
    };
    setMessages((prev) => [...prev, userBubble]);
    setMessage('');
    setLoading(true);
    setError('');

    const url =
      sourceUrl.trim() ||
      selectedWebchat?.site_url ||
      agentV3?.dealer_url ||
      'https://www.example-dealer.ca';

    try {
      const result = await sendAgentChat({
        assistant_id: assistantId,
        thread_id: threadId,
        message: text,
        source: { device, url },
        webchat_pack_id: showWebchatPack ? selectedWebchat?.id || undefined : undefined,
      });
      const reply = result.data?.response || '';
      const nextThread = result.data?.thread_id || threadId;
      setThreadId(nextThread);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          value: reply,
          created_at: Math.floor(Date.now() / 1000),
        },
      ]);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to send message';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showWebchatPack = device === 'webchat' || device === 'sms' || device === 'messaging';
  const showFacebookPack = device === 'facebook';

  return (
    <div className="test-chat">
      <h2>Test Chat</h2>
      <p className="test-chat-hint">
        Calls POST /v1/agent/chat (V3 agents-first). Pick a webchat pack to set source URL for
        ADF / analytics. No Voiceflow.
      </p>

      <div className="test-chat-card">
        <div className="test-chat-grid">
          <div className="form-group">
            <label>Agent</label>
            <AssistantDropdown value={assistantId} onChange={handleAssistantChange} />
          </div>
          <div className="form-group">
            <label>Device (source.device → ADF product)</label>
            <select value={device} onChange={(e) => handleDeviceChange(e.target.value)}>
              {DEVICE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                  {agentV3 && !enabledFlags[opt.product] ? ' (product off)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {agentLoading && <p className="test-chat-empty">Loading agent V3…</p>}

        {agentV3 && (
          <div className="test-chat-flags">
            {['webchat', 'facebook', 'talklive', 'campaign', 'marketplace', 'hook', 'phenix']
              .filter((key) => enabledFlags[key])
              .map((key) => (
                <span key={key} className="test-chat-flag">
                  {key}
                </span>
              ))}
            {!Object.values(enabledFlags).some(Boolean) && (
              <span className="test-chat-flag test-chat-flag-muted">no products enabled</span>
            )}
          </div>
        )}

        {showWebchatPack && webchatPacks.length > 0 && (
          <div className="form-group">
            <label>Webchat pack</label>
            <select
              value={selectedWebchat?.id || ''}
              onChange={(e) => handleWebchatPackChange(e.target.value)}
            >
              {webchatPacks.map((pack, index) => (
                <option key={pack.id || index} value={pack.id}>
                  {packLabel(pack, index)}
                  {pack.site_url ? ` — ${pack.site_url}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {showFacebookPack && facebookPacks.length > 0 && (
          <div className="form-group">
            <label>Facebook pack</label>
            <select
              value={selectedFacebook?.id || ''}
              onChange={(e) => handleFacebookPackChange(e.target.value)}
            >
              {facebookPacks.map((pack, index) => (
                <option key={pack.id || index} value={pack.id}>
                  {packLabel(pack, index)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label>Source URL</label>
          <input
            type="text"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://www.dealer-site.ca/neufs"
          />
          {selectedWebchat?.site_url && showWebchatPack && (
            <p className="test-chat-field-hint">
              Prefills from pack <code>{selectedWebchat.id}</code> site_url (edit freely).
            </p>
          )}
        </div>

        <div className="test-chat-meta">
          <span>Thread: {threadId}</span>
          <button type="button" className="test-chat-secondary" onClick={resetChat}>
            New chat
          </button>
        </div>
      </div>

      <div className="test-chat-messages" ref={listRef}>
        {messages.length === 0 && !loading && (
          <p className="test-chat-empty">Select an agent and send a message.</p>
        )}
        {messages.map((bubble, index) => (
          <MessageItem key={`${bubble.created_at}-${index}`} bubble={bubble} />
        ))}
        {loading && <p className="test-chat-empty">Waiting for reply...</p>}
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="test-chat-composer">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message (Enter to send, Shift+Enter for a new line)"
          rows={3}
          disabled={loading}
        />
        <button type="button" className="test-chat-send" onClick={sendMessage} disabled={loading}>
          Send
        </button>
      </div>
    </div>
  );
}

export default ChatTest;
